import { useCallback, useEffect, useState, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useToast } from "../../../hooks/toast";
import api from "../../../service";

import {
  FormContainer,
  HeaderContainer,
  Header,
  ButtonContainer,
  Text,
} from "./styles";
import { useLoading } from "../../../hooks/loading";
import { Modal } from "antd";
import Button from "../../Defaulters/components/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FormRow } from "../../../components/ControlledInputs/styles";
import ControlledSelect from "../../../components/ControlledInputs/select";
import Copy from "../../../icons/Copy";
import BackIcon from "../../../icons/Back";

const DuplicateChecklistModal = ({
  open = false,
  setOpen,
  targetEventId,
  updateCallback,
}) => {
  const { token } = useSelector((state) => state.auth);
  const intl = useIntl();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const config = useMemo(() => {
    return {
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: `Bearer ${token}`,
      },
    };
  }, [token]);

  const schema = z.object({
    event: z.object({
      value: z.coerce
        .number()
        .min(1, intl.formatMessage({ id: "error.field_required" })),
      label: z.string(),
    }),
  });

  const { control, handleSubmit, formState, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  useEffect(() => {
    if (open) {
      handleLoading(true);
      api
        .get(`/admin/events`, config)
        .then((result) => {
          const events = result.data.output.map((event) => ({
            label: event.eventEditionName,
            value: event.idNumber,
          }));
          let eventsSort = [...events].sort((a, b) =>
            a.label < b.label ? -1 : a.label > b.label ? 1 : 0,
          );
          setEvents(eventsSort);
        })
        .catch((err) => {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          });
        })
        .finally(() => {
          handleLoading(false);
        });
    }
  }, [addToast, intl, handleLoading, open]);

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const handleDuplicateSubmit = useCallback(
    async (data) => {
      try {
        setLoading(true);
        if (loading) {
          return;
        }

        api
          .post(
            "/event-checklist/duplicate",
            {
              sourceEventId: data.event.value,
              targetEventId: Number(targetEventId),
            },
            config,
          )
          .then((result) => {
            if (result.data.code === 33006) {
              addToast({
                type: "warning",
                title: intl.formatMessage({
                  id: "partial_success",
                }),
                description: intl.formatMessage({
                  id: "success." + result.data.code,
                }),
              });
            } else {
              addToast({
                type: "success",
                title: intl.formatMessage({
                  id: "success",
                }),
                description: intl.formatMessage({
                  id: "success." + result.data.code,
                }),
              });
            }

            setOpen(false);
            updateCallback();
            reset();
          })
          .catch((err) => {
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          });
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "flow.unexpected.error",
          }),
        });
      } finally {
        setLoading(false);
      }
    },
    [
      token,
      handleLoading,
      targetEventId,
      updateCallback,
      setOpen,
      addToast,
      intl,
      reset,
    ],
  );

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      centered={true}
      closable={false}
      width={600}
      footer={""}
      className="modalLesson"
      destroyOnClose
    >
      <FormContainer onSubmit={handleSubmit(handleDuplicateSubmit)}>
        <HeaderContainer>
          <Header>
            <FormattedMessage id={"duplicate_existing_checklist"} />
          </Header>
        </HeaderContainer>

        <Text>
          <FormattedMessage id={"select_event"} />
        </Text>

        <FormRow marginBottom={"24px"}>
          <ControlledSelect
            name="event"
            control={control}
            errors={formState.errors}
            label="event_edition"
            placeholder={intl.formatMessage({ id: "event_edition" })}
            required={true}
            options={events}
          />
        </FormRow>
        <ButtonContainer>
          <Button
            label={"goback"}
            variant={"secondary"}
            Icon={<BackIcon size={16} />}
            type={"button"}
            onClick={() => setOpen(false)}
          />
          <Button label={"duplicate"} Icon={<Copy stroke={"#FFF"} />} />
        </ButtonContainer>
      </FormContainer>
    </Modal>
  );
};

export default DuplicateChecklistModal;
